import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import { 
  Users, Search, Filter, BarChart, Star, MessageSquare, Download, ArrowRight, Brain, Target, Award,
  Globe, ArrowLeft, Plus, Phone, Mail, Calendar, ChevronRight
} from 'lucide-react';
import ChatBot from '../../../components/ChatBot';

const translations = {
  es: {
    title: 'Sistema CRM',
    subtitle: 'Gestión de relaciones con clientes',
    sections: {
      contacts: 'Contactos',
      leads: 'Oportunidades',
      deals: 'Negocios',
      tasks: 'Tareas',
      reports: 'Informes'
    },
    actions: {
      newContact: 'Nuevo contacto',
      newLead: 'Nueva oportunidad',
      newDeal: 'Nuevo negocio',
      newTask: 'Nueva tarea',
      back: 'Volver al Dashboard'
    },
    search: 'Buscar...',
    filters: 'Filtros',
    status: {
      active: 'Activo',
      pending: 'Pendiente',
      completed: 'Completado'
    },
    priority: {
      low: 'Baja',
      medium: 'Media',
      high: 'Alta',
      urgent: 'Urgente'
    }
  },
  en: {
    title: 'CRM System',
    subtitle: 'Customer Relationship Management',
    sections: {
      contacts: 'Contacts',
      leads: 'Leads',
      deals: 'Deals',
      tasks: 'Tasks',
      reports: 'Reports'
    },
    actions: {
      newContact: 'New Contact',
      newLead: 'New Lead',
      newDeal: 'New Deal',
      newTask: 'New Task',
      back: 'Back to Dashboard'
    },
    search: 'Search...',
    filters: 'Filters',
    status: {
      active: 'Active',
      pending: 'Pending',
      completed: 'Completed'
    },
    priority: {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      urgent: 'Urgent'
    }
  }
};

// Mock data
const mockContacts = [
  {
    id: 1,
    name: 'Ana García',
    company: 'Tech Corp',
    email: 'ana@techcorp.com',
    phone: '+34 612 345 678',
    status: 'active',
    lastContact: '2025-03-14'
  },
  {
    id: 2,
    name: 'Carlos Martínez',
    company: 'Digital Solutions',
    email: 'carlos@digitalsolutions.com',
    phone: '+34 623 456 789',
    status: 'active',
    lastContact: '2025-03-13'
  }
];

const mockLeads = [
  {
    id: 1,
    company: 'Innovation Labs',
    contact: 'Sarah Johnson',
    value: '50.000€',
    status: 'hot',
    source: 'Website'
  },
  {
    id: 2,
    company: 'Global Tech',
    contact: 'Michael Brown',
    value: '75.000€',
    status: 'warm',
    source: 'Referral'
  }
];

const CRMPage = () => {
  const [activeSection, setActiveSection] = useState('contacts');
  const [showChatbot, setShowChatbot] = useState(false);
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const t = translations[language];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dcvee/dashboard')}
              className="flex items-center text-foreground/70 hover:text-foreground"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              {t.actions.back}
            </button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t.title}</h1>
              <p className="text-foreground/70">{t.subtitle}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Globe className="h-5 w-5 text-foreground/70" />
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as 'en' | 'es')}
                className="bg-secondary/30 border border-border rounded-lg px-3 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
            <button
              onClick={() => setShowChatbot(true)}
              className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              AI Assistant
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
              <Plus className="h-4 w-4 mr-2" />
              {t.actions.newContact}
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder={t.search}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-foreground/50" />
          </div>
          <div className="flex space-x-4">
            <button className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30">
              <Filter className="h-4 w-4 mr-2" />
              {t.filters}
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30">
              <BarChart className="h-4 w-4 mr-2" />
              {t.sections.reports}
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-border mb-8">
          <nav className="-mb-px flex space-x-8">
            {Object.entries(t.sections).map(([key, value]) => (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeSection === key
                    ? 'border-accent text-accent'
                    : 'border-transparent text-foreground/70 hover:text-foreground hover:border-border'
                }`}
              >
                {value}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Section */}
        {activeSection === 'contacts' && (
          <div className="space-y-6">
            {mockContacts.map((contact) => (
              <div
                key={contact.id}
                className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Users className="h-5 w-5 text-accent" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{contact.name}</h3>
                      <p className="text-foreground/70">{contact.company}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button className="text-foreground/70 hover:text-foreground">
                      <Phone className="h-5 w-5" />
                    </button>
                    <button className="text-foreground/70 hover:text-foreground">
                      <Mail className="h-5 w-5" />
                    </button>
                    <button className="text-foreground/70 hover:text-foreground">
                      <Calendar className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-foreground/70">Email</p>
                    <p className="text-sm font-medium text-foreground">{contact.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground/70">Phone</p>
                    <p className="text-sm font-medium text-foreground">{contact.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground/70">Last Contact</p>
                    <p className="text-sm font-medium text-foreground">{contact.lastContact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeSection === 'leads' && (
          <div className="space-y-6">
            {mockLeads.map((lead) => (
              <div
                key={lead.id}
                className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Star className="h-5 w-5 text-accent" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{lead.company}</h3>
                      <p className="text-foreground/70">{lead.contact}</p>
                    </div>
                  </div>
                  <div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      lead.status === 'hot'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {lead.status.toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-foreground/70">Value</p>
                    <p className="text-sm font-medium text-foreground">{lead.value}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground/70">Source</p>
                    <p className="text-sm font-medium text-foreground">{lead.source}</p>
                  </div>
                  <div className="flex justify-end">
                    <button className="inline-flex items-center text-accent hover:text-accent/90">
                      View Details
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chatbot */}
      {showChatbot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <ChatBot 
              role="crm"
              onClose={() => setShowChatbot(false)} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMPage;